import type { ErrorObject } from 'ajv';

export type SettingsSection = 'vendorsettings' | 'usersettings';

export type SchemaDriftIssue = {
  section: SettingsSection;
  fullPath: string;
  relativePath: string;
};

export type SchemaDriftGroup = {
  section: SettingsSection;
  issues: SchemaDriftIssue[];
};

type AdditionalPropertiesError = ErrorObject<'additionalProperties', { additionalProperty: string }, unknown>;

const settingsSectionOrder: SettingsSection[] = ['vendorsettings', 'usersettings'];

const decodeJsonPointerSegment = (segment: string): string => segment.replace(/~1/g, '/').replace(/~0/g, '~');

const jsonPointerToSegments = (pointer: string): string[] =>
  pointer.split('/').slice(1).filter(Boolean).map(decodeJsonPointerSegment);

const formatPathFromSegments = (segments: string[]): string =>
  segments.reduce((path, segment) => {
    if (/^\d+$/.test(segment)) {
      return `${path}[${segment}]`;
    }

    return path ? `${path}.${segment}` : segment;
  }, '');

const isSettingsSection = (value: string | undefined): value is SettingsSection =>
  value === 'vendorsettings' || value === 'usersettings';

const isAdditionalPropertiesError = (error: ErrorObject): error is AdditionalPropertiesError =>
  error.keyword === 'additionalProperties' &&
  typeof (error.params as { additionalProperty?: unknown }).additionalProperty === 'string';

const getAdditionalProperty = (error: AdditionalPropertiesError): string => error.params.additionalProperty;

const getErrorLocation = (instancePath: string): string =>
  formatPathFromSegments(jsonPointerToSegments(instancePath)) || 'settings';

export const extractSchemaDriftIssues = (errors: readonly ErrorObject[] | null | undefined): SchemaDriftIssue[] => {
  const issues = new Map<string, SchemaDriftIssue>();

  (errors ?? []).forEach((error) => {
    if (!isAdditionalPropertiesError(error)) {
      return;
    }

    const pathSegments = jsonPointerToSegments(error.instancePath);
    const [section, ...nestedSegments] = pathSegments;

    if (!isSettingsSection(section)) {
      return;
    }

    const additionalProperty = getAdditionalProperty(error);
    const relativePath = formatPathFromSegments([...nestedSegments, additionalProperty]);
    const fullPath = formatPathFromSegments([section, ...nestedSegments, additionalProperty]);

    issues.set(fullPath, {
      section,
      fullPath,
      relativePath,
    });
  });

  return Array.from(issues.values()).sort((left, right) => left.fullPath.localeCompare(right.fullPath));
};

export const groupSchemaDriftIssues = (issues: readonly SchemaDriftIssue[]): SchemaDriftGroup[] =>
  settingsSectionOrder
    .map((section) => ({
      section,
      issues: issues.filter((issue) => issue.section === section),
    }))
    .filter((group) => group.issues.length > 0);

export const isSchemaDriftError = (error: ErrorObject): boolean => extractSchemaDriftIssues([error]).length > 0;

export const buildValidationErrorKey = (error: ErrorObject): string => {
  const additionalProperty = isAdditionalPropertiesError(error) ? getAdditionalProperty(error) : undefined;

  return JSON.stringify({
    keyword: error.keyword,
    instancePath: error.instancePath,
    schemaPath: error.schemaPath,
    additionalProperty,
  });
};

export const formatValidationErrorMessage = (error: ErrorObject): string => {
  if (isAdditionalPropertiesError(error)) {
    const additionalProperty = getAdditionalProperty(error);
    const location = formatPathFromSegments([...jsonPointerToSegments(error.instancePath), additionalProperty]);

    return `${location} is not allowed by the current schema`;
  }

  return `${getErrorLocation(error.instancePath)}: ${error.message ?? 'validation error'}`;
};
